import React, { useState, useRef, useEffect } from 'react';

/**
 * RefreshPricesButton - Triggers real-time price refresh via Yahoo Finance
 * and reruns the rebalancer with updated prices.
 * 
 * Supports two modes:
 * - "all" : Starts a background job and polls for completion.
 *           Results are saved to S3 by the backend; once done
 *           the caller should refresh summaries from S3.
 * - "group" : Refreshes prices and rebalances a single group
 *             synchronously, returning the result inline.
 * 
 * @param {string} mode - "all" or "group"
 * @param {string} groupName - Required when mode is "group"
 * @param {function} onRefreshComplete - Callback after successful refresh (receives result data)
 * @param {string} className - Additional CSS classes
 * @param {string} size - Button size: "sm", "md" (default), "lg"
 * @param {boolean} disabled - External disable flag
 * @param {boolean} buyToMax - When true, the rerun allows invest-cash buys up to MAXIMUM target weights
 * @param {string} label - Optional custom button label
 * @param {string} variant - Bootstrap button variant (default "outline-warning")
 * @param {string} icon - Bootstrap icon class (default "bi-currency-dollar")
 */
const RefreshPricesButton = ({ 
  mode = 'all', 
  groupName = null, 
  onRefreshComplete = null,
  className = '',
  size = 'md',
  disabled = false,
  buyToMax = false,
  label = null,
  variant = 'outline-warning',
  icon = 'bi-currency-dollar'
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');

  // Track whether the component is still mounted so we can
  // safely cancel the polling loop on unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * Poll a background refresh-all job until it completes or fails.
   *
   * @param {object} rrService - The rrService module (already imported).
   * @param {string} jobId - Job ID returned by the POST endpoint.
   * @returns {object} Final job status.
   */
  const pollJob = async (rrService, jobId) => {
    const POLL_INTERVAL_MS = 5000;
    const MAX_ATTEMPTS = 180; // ~15 minutes (ECS tasks need startup time)

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      if (!mountedRef.current) return null;

      const status = await rrService.pollRefreshPricesStatus(jobId);

      // Surface progress to the user
      if (status.progress && mountedRef.current) {
        setProgress(status.progress);
      }

      if (status.status === 'completed') return status;
      if (status.status === 'failed') {
        throw new Error(status.error || 'Background job failed');
      }
      // Otherwise keep polling (status is "started" or "running")
    }

    throw new Error('Price refresh timed out after 15 minutes');
  };

  // -------------------------------------------------------------- //
  // On mount: check DynamoDB for an already-running refresh job.    //
  // If another session kicked one off we pick it up automatically.  //
  // -------------------------------------------------------------- //
  useEffect(() => {
    let cancelled = false;

    const checkForActiveJob = async () => {
      try {
        const rrService = (await import('../services/rrService')).default;
        const data = await rrService.checkActiveRefreshJob();

        if (cancelled || !mountedRef.current) return;

        if (data?.active && data.job_id) {
          setIsRefreshing(true);
          setProgress(data.progress || 'Resuming in-progress refresh…');

          try {
            const result = await pollJob(rrService, data.job_id);
            if (!mountedRef.current) return;
            if (result) {
              setLastResult(result);
              if (onRefreshComplete) onRefreshComplete(result);
            }
          } catch (err) {
            if (mountedRef.current) {
              setError(err.message || 'Active job failed');
            }
          } finally {
            if (mountedRef.current) {
              setIsRefreshing(false);
              setProgress('');
            }
          }
        }
      } catch (err) {
        console.error('Error checking for active refresh job:', err);
      }
    };

    checkForActiveJob();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doRefresh = async () => {
    const rrService = (await import('../services/rrService')).default;

    // ---- Guard: check if a job is already running ---- //
    try {
      const activeCheck = await rrService.checkActiveRefreshJob();
      if (activeCheck?.active && activeCheck.job_id) {
        // Another session (or this one) already started a refresh —
        // just start polling that job instead of launching a new one.
        setIsRefreshing(true);
        setError(null);
        setLastResult(null);
        setProgress(activeCheck.progress || 'Joining in-progress refresh…');

        try {
          const result = await pollJob(rrService, activeCheck.job_id);
          if (!mountedRef.current) return;
          if (result) {
            setLastResult(result);
            if (onRefreshComplete) onRefreshComplete(result);
          }
        } catch (err) {
          if (mountedRef.current) {
            const message = err.response?.data?.detail || err.message || 'Failed to refresh prices';
            setError(message);
          }
        } finally {
          if (mountedRef.current) {
            setIsRefreshing(false);
            setProgress('');
          }
        }
        return;
      }
    } catch (err) {
      // If the active check fails, just proceed with launching
      console.warn('Active-job check failed, proceeding with launch:', err);
    }

    setIsRefreshing(true);
    setError(null);
    setLastResult(null);
    setProgress('');

    try {
      let result;

      if (mode === 'group' && groupName) {
        // Single-group: launches ECS task, returns job_id
        setProgress('Launching price refresh…');
        const jobResponse = await rrService.refreshPricesForGroup(groupName, { buyToMax });

        if (!jobResponse?.job_id) {
          throw new Error('No job_id returned from backend');
        }

        result = await pollJob(rrService, jobResponse.job_id);
        if (!result) return; // component unmounted
      } else {
        // All-groups: launches ECS task, returns job_id
        setProgress('Launching price refresh…');
        const jobResponse = await rrService.refreshPricesForAll();

        if (!jobResponse?.job_id) {
          throw new Error('No job_id returned from backend');
        }

        result = await pollJob(rrService, jobResponse.job_id);
        if (!result) return; // component unmounted
      }

      if (mountedRef.current) {
        setLastResult(result);
        if (onRefreshComplete) {
          onRefreshComplete(result);
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        const message = err.response?.data?.detail || err.message || 'Failed to refresh prices';
        setError(message);
        console.error('Price refresh error:', err);
      }
    } finally {
      if (mountedRef.current) {
        setIsRefreshing(false);
        setProgress('');
      }
    }
  };

  const sizeClass = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';
  const buttonLabel = label || (mode === 'group' ? 'Refresh Prices' : 'Refresh All Prices');

  return (
    <div className="d-inline-flex align-items-center gap-2">
      <button
        className={`btn btn-${variant} ${sizeClass} ${className}`}
        onClick={doRefresh}
        disabled={isRefreshing || disabled}
        title={mode === 'group' 
          ? `Fetch real-time prices for ${groupName} and rerun rebalancer${buyToMax ? ' allowing buys up to maximum targets' : ''}` 
          : 'Fetch real-time prices for all held assets and rerun rebalancer'}
      >
        {isRefreshing ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {progress || 'Refreshing…'}
          </>
        ) : (
          <>
            <i className={`bi ${icon} me-1`}></i>
            {buttonLabel}
          </>
        )}
      </button>

      {/* Error indicator */}
      {error && (
        <span className="badge bg-danger" title={error} style={{ cursor: 'pointer' }} onClick={() => setError(null)}>
          <i className="bi bi-exclamation-triangle me-1"></i>
          Error
        </span>
      )}
    </div>
  );
};

export default RefreshPricesButton;
